import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BarList,
  Button,
  Card,
  ChartCard,
  DeviceLink,
  EmptyState,
  Input,
  KpiTile,
  Pagination,
  RangePicker,
  Spinner,
  Table,
  Td,
  Th,
} from '@/components';
import type {
  LearningBreakdownsResponse,
  LearningEventsResponse,
  LearningSummaryResponse,
  LearningTimeseriesResponse,
  Range,
} from '@/lib/admin-types';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatAdminDateTime, formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

const LEARNING_HINTS = {
  kpiTaps:
    'Count of learning hub resource tap events in the current period (Pacific calendar days, same length as the range), compared to the prior period.',
  kpiViews: 'Count of flashcard screen impression events in the same rolling window as totals above.',
  kpiDistTap:
    'Distinct devices (installations) with at least one resource tap in the rolling window. Delta vs the prior period.',
  kpiDistView:
    'Distinct devices with at least one flashcard view in the rolling window. Delta vs the prior period.',
  chartDaily:
    'Calendar days in America/Los_Angeles: daily counts of resource taps vs flashcard views. KPI totals use the same Pacific window as the chart.',
  barResource: 'Resource modal keys by tap count (top 10 + other).',
  barCategory: 'Tap counts by hub category string.',
  barCollection: 'Flashcard views by deck/collection name.',
  barCard: 'Views by persistency key when present, else card title (top 10 + other).',
  events:
    'Raw events in the rolling window (same range as KPIs). Filter by tap vs flashcard or search substrings.',
} as const;

type LearningSearch = {
  range: Range;
  page: number;
  q: string;
  filter: 'all' | 'tap' | 'view';
};

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function chartTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, String(name)] as [string, string];
}

function LearningPage() {
  const { range, page, q, filter } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/learning' });
  const qc = useQueryClient();
  const [qDraft, setQDraft] = useState(q);

  useEffect(() => {
    setQDraft(q);
  }, [q]);

  const summaryQ = useQuery({
    queryKey: ['admin', 'learning', 'summary', range],
    queryFn: () =>
      apiJson<LearningSummaryResponse>(
        `/v1/admin/analytics/learning/summary?range=${encodeURIComponent(range)}`
      ),
  });

  const tsQ = useQuery({
    queryKey: ['admin', 'learning', 'timeseries', range],
    queryFn: () =>
      apiJson<LearningTimeseriesResponse>(
        `/v1/admin/analytics/learning/timeseries?range=${encodeURIComponent(range)}`
      ),
  });

  const brQ = useQuery({
    queryKey: ['admin', 'learning', 'breakdowns', range],
    queryFn: () =>
      apiJson<LearningBreakdownsResponse>(
        `/v1/admin/analytics/learning/breakdowns?range=${encodeURIComponent(range)}`
      ),
  });

  const eventsQ = useQuery({
    queryKey: ['admin', 'learning', 'events', range, page, q, filter],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('range', range);
      p.set('page', String(page));
      p.set('page_size', '25');
      p.set('type', filter);
      if (q.trim()) p.set('q', q.trim());
      return apiJson<LearningEventsResponse>(
        `/v1/admin/analytics/learning/events?${p.toString()}`
      );
    },
  });

  const loading = summaryQ.isLoading || tsQ.isLoading || brQ.isLoading;
  const err = summaryQ.error || tsQ.error || brQ.error || eventsQ.error;

  const summary = summaryQ.data;
  const ts = tsQ.data;
  const br = brQ.data;

  const chartRows = useMemo(
    () =>
      ts?.days.map((d) => ({
        ...d,
        label: fmtDay(d.date),
      })) ?? [],
    [ts]
  );

  const sparkTaps = ts?.days.map((d) => d.resource_taps) ?? [];
  const sparkViews = ts?.days.map((d) => d.flashcard_views) ?? [];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Learning</h1>
          <p className="mt-1 text-sm text-gray-500">
            Resource hub taps and flashcard impressions — KPIs and charts use the same Pacific calendar window.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={range}
            onChange={(r) => {
              void navigate({
                search: (prev) => ({ ...prev, range: r, page: 1 }),
              });
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'learning'] });
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-600">
          {(err as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void qc.invalidateQueries({ queryKey: ['admin', 'learning'] })}
          >
            Retry
          </button>
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Resource taps"
          value={summary?.resource_tap_count ?? 0}
          delta={summary?.delta_resource_taps}
          spark={sparkTaps.slice(-14)}
          loading={loading}
          tooltip={LEARNING_HINTS.kpiTaps}
        />
        <KpiTile
          label="Flashcard views"
          value={summary?.flashcard_view_count ?? 0}
          delta={summary?.delta_flashcard_views}
          spark={sparkViews.slice(-14)}
          loading={loading}
          tooltip={LEARNING_HINTS.kpiViews}
        />
        <KpiTile
          label="Distinct tap devices"
          value={summary?.distinct_tap_installations ?? 0}
          delta={summary?.delta_distinct_tap_installations}
          loading={loading}
          tooltip={LEARNING_HINTS.kpiDistTap}
        />
        <KpiTile
          label="Distinct view devices"
          value={summary?.distinct_view_installations ?? 0}
          delta={summary?.delta_distinct_view_installations}
          loading={loading}
          tooltip={LEARNING_HINTS.kpiDistView}
        />
      </div>

      <ChartCard
        title="Daily taps & views (Pacific)"
        tooltip={LEARNING_HINTS.chartDaily}
        loading={loading}
        error={tsQ.error ? String(tsQ.error) : undefined}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis
              yAxisId="left"
              allowDecimals={false}
              width={44}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatInt(Number(v))}
            />
            <YAxis yAxisId="right" orientation="right" allowDecimals={false} width={36} hide />
            <Tooltip formatter={chartTooltipFormatter} />
            <Legend />
            <Bar
              yAxisId="left"
              dataKey="resource_taps"
              name="Resource taps"
              fill={PRIMARY_HEX}
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="flashcard_views"
              name="Flashcard views"
              stroke="#0ea5e9"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card padding="md" className="cursor-help" title={LEARNING_HINTS.barResource}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">Top resource keys</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
          ) : !br?.by_resource_key.length ? (
            <EmptyState title="No taps in window" />
          ) : (
            <BarList
              data={br.by_resource_key.map((e) => ({ key: e.key, count: e.count }))}
            />
          )}
        </Card>
        <Card padding="md" className="cursor-help" title={LEARNING_HINTS.barCategory}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">By category</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
          ) : !br?.by_category.length ? (
            <EmptyState title="No taps in window" />
          ) : (
            <BarList data={br.by_category.map((e) => ({ key: e.key, count: e.count }))} />
          )}
        </Card>
        <Card padding="md" className="cursor-help" title={LEARNING_HINTS.barCollection}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">Top collections</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
          ) : !br?.by_collection.length ? (
            <EmptyState title="No views in window" />
          ) : (
            <BarList
              data={br.by_collection.map((e) => ({ key: e.key, count: e.count }))}
            />
          )}
        </Card>
        <Card padding="md" className="cursor-help" title={LEARNING_HINTS.barCard}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">Top cards</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
          ) : !br?.by_card.length ? (
            <EmptyState title="No views in window" />
          ) : (
            <BarList data={br.by_card.map((e) => ({ key: e.key, count: e.count }))} />
          )}
        </Card>
      </div>

      <Card padding="md" className="cursor-help" title={LEARNING_HINTS.events}>
        <h2 className="mb-4 text-sm font-semibold text-gray-800">Events</h2>
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="min-w-0 flex-1">
            <Input
              label="Search (resource, category, collection, title)"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void navigate({
                    search: (prev) => ({
                      ...prev,
                      q: qDraft,
                      page: 1,
                    }),
                  });
                }
              }}
              placeholder="filter"
            />
          </div>
          <div className="grid w-full gap-2 sm:grid-cols-2 lg:w-auto lg:min-w-[14rem]">
            <div>
              <label className="mb-1 block text-sm text-gray-500">Event type</label>
              <select
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filter}
                onChange={(e) => {
                  const v = e.target.value as 'all' | 'tap' | 'view';
                  void navigate({
                    search: (prev) => ({ ...prev, filter: v, page: 1 }),
                  });
                }}
              >
                <option value="all">All</option>
                <option value="tap">Resource taps</option>
                <option value="view">Flashcard views</option>
              </select>
            </div>
            <Button
              variant="secondary"
              className="lg:mb-0"
              onClick={() =>
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    q: qDraft,
                    page: 1,
                  }),
                })
              }
            >
              Apply search
            </Button>
          </div>
        </div>

        {eventsQ.isLoading && (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        )}
        {eventsQ.isError && (
          <p className="text-sm text-red-600">
            {(eventsQ.error as Error).message}
            <button type="button" className="ml-2 underline" onClick={() => void eventsQ.refetch()}>
              Retry
            </button>
          </p>
        )}
        {eventsQ.data && eventsQ.data.items.length === 0 && <EmptyState title="No events match" />}
        {eventsQ.data && eventsQ.data.items.length > 0 && (
          <>
            <div className="max-w-full overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>When</Th>
                    <Th>Type</Th>
                    <Th>Detail</Th>
                    <Th>Device</Th>
                  </tr>
                </thead>
                <tbody>
                  {eventsQ.data.items.map((row, idx) => (
                    <tr key={`${row.at}-${row.installation_id}-${idx}`}>
                      <Td className="whitespace-nowrap text-xs text-gray-500">
                        {formatAdminDateTime(row.at)}
                      </Td>
                      <Td className="text-sm">{row.type === 'tap' ? 'Tap' : 'View'}</Td>
                      <Td className="max-w-[18rem] text-sm">
                        {row.type === 'tap' ? (
                          <>
                            <span className="font-mono text-xs">{row.resource_key}</span>
                            {row.category ? (
                              <span className="ml-1 text-xs text-gray-500">({row.category})</span>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-gray-600">{row.collection}</span>
                            <span className="mx-1 text-gray-400">—</span>
                            <span>{row.card_title}</span>
                          </>
                        )}
                      </Td>
                      <Td>
                        <DeviceLink
                          installationId={row.installation_id}
                          nickname={row.nickname}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Pagination
              page={eventsQ.data.page}
              pageSize={eventsQ.data.page_size}
              total={eventsQ.data.total}
              onChange={(p) =>
                void navigate({
                  search: (prev) => ({ ...prev, page: p }),
                })
              }
            />
          </>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/admin/learning')({
  validateSearch: (raw: Record<string, unknown>): LearningSearch => {
    const r = raw.range;
    const range: Range = r === '30d' || r === '90d' || r === '7d' ? r : '7d';
    const pageRaw = raw.page;
    const pageNum =
      typeof pageRaw === 'number' && !Number.isNaN(pageRaw)
        ? pageRaw
        : typeof pageRaw === 'string'
          ? parseInt(pageRaw, 10) || 1
          : 1;
    const q = typeof raw.q === 'string' ? raw.q : '';
    const f = raw.filter;
    const filter: 'all' | 'tap' | 'view' =
      f === 'tap' || f === 'view' ? f : 'all';
    return {
      range,
      page: Math.max(1, pageNum),
      q,
      filter,
    };
  },
  component: LearningPage,
});
